// What a placed template writes into its Roll20 GM Notes.
//
// Two things, in this order:
//   1. what the ENGINE will actually roll — shape, dice, save, triggers
//   2. what the SPELL says — its D&D Beyond text if this account owns it, a
//      link to the page if not
//
// The order is the point. A template that quietly rolls something other than
// the spell is the failure this guards against (the 2026-09-26 audit found
// three of them, hand-written), so the numbers the engine holds are stated
// first and the book text sits under them for comparison. The DM can see a
// disagreement on the map instead of discovering it when a player takes damage.
//
// Entitlement needs no rule here: D&D Beyond only returns spells the account
// owns, so a miss from the connector IS "you do not have this one", and the
// note falls back to a link. The prose itself is licensed to that account and
// is never stored — it is fetched per session and written only into the DM's
// own Roll20 game.
import type { EffectDef } from "./effects";

/** Our block inside a GM Notes field. Anything outside it belongs to the DM. */
export const NOTE_OPEN = "<!--dmscreen-->";
export const NOTE_CLOSE = "<!--/dmscreen-->";

export interface SpellText { owned: boolean; name?: string; id?: number; description?: string }

const esc = (v: unknown) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const slug = (v: string) => String(v || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const ABILITY: Record<string, string> = { str: "Strength", dex: "Dexterity", con: "Constitution", int: "Intelligence", wis: "Wisdom", cha: "Charisma" };

/** The spell on D&D Beyond: its own page when the id is known, else a search. */
export const ddbSpellUrl = (name: string, id?: number) =>
  id ? `https://www.dndbeyond.com/spells/${id}-${slug(name)}` : `https://www.dndbeyond.com/spells?filter-search=${encodeURIComponent(name)}`;

/** One line of what the engine holds for this template, in the DM's words. */
export function templateLine(def: EffectDef, dice: string, dc: number): string {
  const bits: string[] = [];
  bits.push(`${def.sizeFt} ft ${def.shape}`);
  if (dice) bits.push(`${dice}${def.dmgType ? " " + def.dmgType : ""}`);
  if (def.save) bits.push(`DC ${dc} ${ABILITY[def.save] || def.save} save${def.dice || dice ? (def.half ? ", half on a success" : ", none on a success") : ""}`);
  else if (dice) bits.push("no save");
  if (def.condition) bits.push(def.condition);
  bits.push(def.triggers.length ? `fires on: ${def.triggers.join(", ")}` : "no automatic trigger");
  if (def.concentration) bits.push("concentration");
  return bits.join(" · ");
}

/** Strip what must never ride into a Roll20 field, and cap the size. */
const safeHtml = (html: string, cap = 12000) => {
  const cleaned = String(html || "")
    .replace(/<\s*(script|style|iframe|object|embed)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/javascript:/gi, "");
  return cleaned.length > cap ? cleaned.slice(0, cap) + "\n<p><i>…truncated; open the spell on D&amp;D Beyond for the rest.</i></p>" : cleaned;
};

/** The whole block, markers included, ready to hand to the place op. */
export function spellNoteHtml(def: EffectDef, dice: string, dc: number, text: SpellText): string {
  const name = text.name || def.name;
  const head = `<p><b>${esc(name)}</b> — placed by the DM Screen</p>`;
  const rolls = `<p><b>This template rolls:</b> ${esc(templateLine(def, dice, dc))}</p>`;
  const caveat = def.note ? `<p><b>Where it differs from the spell:</b> ${esc(def.note)}</p>` : "";
  const body = text.owned && text.description
    ? `<hr>${safeHtml(text.description)}`
    : `<hr><p>${text.owned ? "D&amp;D Beyond returned no text for this spell" : "Not on this D&amp;D Beyond account"} — <a href="${esc(ddbSpellUrl(name, text.id))}">open ${esc(name)} on D&amp;D Beyond</a>.</p>`;
  return NOTE_OPEN + head + rolls + caveat + body + NOTE_CLOSE;
}

/** Replace only our block, so notes the DM typed on the token survive a re-place. */
export function mergeNote(current: string, ours: string): string {
  const cur = String(current || "");
  const i = cur.indexOf(NOTE_OPEN), j = cur.indexOf(NOTE_CLOSE);
  const theirs = (i >= 0 && j > i ? cur.slice(0, i) + cur.slice(j + NOTE_CLOSE.length) : cur).trim();
  if (!ours) return theirs;
  return theirs ? ours + theirs : ours;
}
